import type { ExpressionSpecification } from 'maplibre-gl';
import type { VehicleType } from './types';
import type { MarkerTone } from './motion';

/**
 * Top-down vehicle markers for the live map — the SHAPE axis of a marker, and the
 * machinery that colours and rasterises it.
 *
 * TWO AXES, never conflated (Mahad's ruling, PLAN S2). This module encodes the
 * ruling as a pure function, `resolveIcon(type, tone)`:
 *   - SHAPE (`glyph`)      depends ONLY on `vehicleType`  — what the vehicle IS.
 *   - COLOUR (`colorToken`) depends ONLY on the motion tone — what it is DOING.
 * Neither reads the other; that independence is what the paired axis tests assert.
 *
 * SLICE 58 — MARKERS V2 (Mahad's redesigned silhouettes, 24 Sep). Every type has
 * its own shape now, GENERATED from docs/design/vehicles/*.svg by
 * ci/gen-vehicle-glyphs.mjs into this file AND the app's (identical by
 * construction; CI fails on drift):
 *   sedan → SEDAN   suv → SUV   land_cruiser → LANDCRUISER   pickup → PICKUP
 *   van → MINIBUS   truck → TRUCK   tanker → WATERTANKER   bus → BUS
 *   motorcycle → MOTORBIKE   other → GENERIC (a navigation arrow)
 *   three_wheeler → THREEWHEEL (unchanged, hand-drawn — Mahad: "the bajaj is ok")
 * The DOT survives only for an unrecognised wire value. A new `VehicleType` is a
 * COMPILE error in `GLYPH_FOR_TYPE` below.
 *
 * THREEWHEEL → its own glyph (slice 55, the Bajaj/tuk-tuk). No artboard exists, so
 * it was drawn to the same discipline the van rejection teaches: the cue must
 * survive ROTATION, because markers only ever render rotated to course. Its cue is
 * the FRONT↔REAR ASYMMETRY of the OUTLINE — a narrow, pointed single-front-wheel
 * nose flaring to a wide, flat, two-rear-wheel tail (a teardrop/arrowhead). Unlike
 * van's nose-glass (an interior mark that vanished when rotated), an asymmetric
 * outline is a property at EVERY angle. It was rotation-checked against moto and car
 * at DPR1/DPR2 (slice 55 report) before shipping.
 *
 * WHAT THE MAP DRAWS — SLICE 56c (Mahad, 20 Sep, supersedes the state-table's dot
 * fallbacks; see geojson.ts `resolveMarker`). A known type ALWAYS draws its TYPE
 * silhouette — moving (rotated to the current course), idle/stopped/stale/unknown
 * (rotated to `lastMovingCourse`) and offline (its own grey, FILLED) all draw the shape. A vehicle
 * that has NEVER been observed moving draws its silhouette NORTH-UP as an honest
 * placeholder, corrected on the first moving fix. The DOT survives ONLY for
 * `other`/unclassified types. Silhouette COLOUR is the four-state map language: green
 * (moving), amber (idling) and one grey for every not-moving state (offline the same
 * grey, all SOLID) — so this module rasterises a silhouette per (glyph × PAINT CLASS),
 * where the paint class folds stopped/stale/unknown into one `still` image.
 *
 * VAN → MINIBUS (slice 58). Before 58 `van` drew the CAR, because the earlier van
 * silhouette's cue did not survive rotation. Mahad's redesigned set (24 Sep,
 * docs/design/vehicles) ships a MINIBUS with a distinct long body and side windows,
 * and `van` now maps to it — his design decision, and the glyph is drawn rotated to
 * course like every other.
 *
 * COLOUR IS TOKENISED. Nothing here bakes a hex colour. Body/glass/keyline are
 * resolved from `--raad-*` custom properties at runtime (getComputedStyle),
 * exactly as slice 10a's trail bands — so the queued theme slice re-points every
 * marker colour in one place, and no `#` literal appears in this file. Body fill
 * carries the MOTION colour; glass and keyline are NEUTRAL (a dark-slate glass and
 * a white keyline) precisely because colour is the motion axis alone.
 *
 * GEOMETRY is extracted VERBATIM from docs/design/vehicle-icons.html (viewBox
 * 40×72, nose pointing up/north at rotation 0). Only the SILHOUETTE-defining
 * elements are kept — body, glass/windscreen, body-break, tanker end-caps + manhole,
 * moto wheels + helmet. The reference's decorative shade gradient, white-highlight
 * overlays and partial-opacity fills are DROPPED for the rasterised map image: they
 * turn to mud at 24–40 px and would each need a non-token colour. One deliberate
 * departure remains, documented at its call site: the TRUCK sm cargo box is
 * shortened so the cab→box gap survives when the marker is small.
 */

/** The glyphs. `dot` is producible by the atlas but the map never adds it as an
 *  image — it is the existing circle layer, kept for an unrecognised type. */
export type Glyph =
  | 'sedan'
  | 'suv'
  | 'landcruiser'
  | 'pickup'
  | 'minibus'
  | 'truck'
  | 'watertanker'
  | 'bus'
  | 'motorbike'
  | 'generic'
  | 'threewheel'
  | 'dot';

/** The eleven DRAWN silhouettes — everything except the dot. */
export const SILHOUETTE_GLYPHS: readonly Exclude<Glyph, 'dot'>[] = [
  'sedan',
  'suv',
  'landcruiser',
  'pickup',
  'minibus',
  'truck',
  'watertanker',
  'bus',
  'motorbike',
  'generic',
  'threewheel',
] as const;

/**
 * The exhaustive type → glyph map. Being a `Record<VehicleType, Glyph>`, an
 * eleventh `VehicleType` is a COMPILE error here — the "a new type must be a compile
 * error" rule, encoded structurally rather than as a runtime default. `van` maps to
 * `minibus` (see the van→minibus note); `other` maps to the `generic` arrow; only an
 * unexpected wire value (via `glyphForType`'s fallback) resolves to the dot.
 */
export const GLYPH_FOR_TYPE: Record<VehicleType, Glyph> = {
  sedan: 'sedan',
  suv: 'suv',
  land_cruiser: 'landcruiser',
  pickup: 'pickup',
  van: 'minibus',
  truck: 'truck',
  tanker: 'watertanker',
  bus: 'bus',
  motorcycle: 'motorbike',
  three_wheeler: 'threewheel',
  other: 'generic',
};

/** The glyph for a vehicle type; an unexpected wire value → dot. */
export function glyphForType(type: VehicleType): Glyph {
  return GLYPH_FOR_TYPE[type] ?? 'dot';
}

/* ── The motion-colour axis: tone → CSS custom-property NAME ────────────────── */

/**
 * The map's states: moving, idling, NOT MOVING, NO CONTACT (+ speeding).
 *
 * 🔴 NO STATE IS EVER HOLLOW. Mahad, 21 Sep: *"i dont want hollow at all, device
 * should be seen at all times, you already have the state colours why hollow"*.
 * 56c drew no-contact as an empty outline and 56d found it **invisible against
 * satellite imagery** — a customer simply could not see the vehicle. Every state is
 * a SOLID body now, and the state is carried by COLOUR alone.
 *
 * 🔴 THAT FORCES TWO GREYS, which is the whole of this change. While no-contact was
 * hollow it could share `--raad-marker-still` with parked, because shape did the
 * separating. Filled, nothing else distinguishes "tracker dead" from "parked", so:
 *   stopped / stale / unknown → `--raad-marker-parked` (56f: split from the `--raad-slate` text colour)
 *   offline                   → `--raad-marker-still` (darker — no contact)
 * These are slice 56d's assignments, adopted VERBATIM rather than re-derived, so the
 * two branches cannot ship contradicting schemes. Do not fold them back together.
 *
 * A seventh tone would be a compile error (missing key). The DOM status dots keep six
 * distinct `--raad-motion-*` tones — the WORD beside them carries the finer
 * distinction, readable in text but not at 8 px (§4).
 */
export const TONE_COLOR_TOKEN: Record<MarkerTone, string> = {
  // 🔴 SLICE 84 — DECOUPLED FROM CHROME. The moving tone was `--raad-primary`,
  // which is the business GREEN on web but the admin INDIGO on admin. Global-live
  // parity (and the one-vocabulary rule) requires a moving vehicle to read GREEN on
  // BOTH apps, so the moving tone now reads a dedicated `--raad-marker-moving` token
  // (the same green value in web AND admin styles.css). Chrome stays each app's own
  // `--raad-primary`.
  moving: '--raad-marker-moving',
  idle: '--raad-warn',
  stopped: '--raad-marker-parked',
  unknown: '--raad-marker-parked',
  offline: '--raad-marker-still',
  stale: '--raad-marker-parked',
};

export const MARKER_TONES: readonly MarkerTone[] = [
  'moving',
  'idle',
  'stopped',
  'unknown',
  'offline',
  'stale',
] as const;

/**
 * SLICE 56c — the marker PAINT CLASS: the map's visible states, which key the
 * atlas (a still car and a stale car rasterise to the SAME image, so there is one,
 * not two). `offline` is its own class because it takes its own grey; the other three
 * not-moving tones fold into `still`.
 *
 * 🔴 SLICE B (21 Sep 2026) — `speeding` is the FIFTH class, and it is the first one
 * that is NOT a function of `MarkerTone`. Speeding is not a motion: a speeding
 * vehicle IS moving. So it is deliberately NOT reachable from `paintClassForTone`
 * (which maps tone → class and can never return it); the decision is made in
 * `geojson.ts`'s `toFeature`, where the live payload's `speeding` flag is in scope.
 * The load-bearing half of the two-axis ruling is untouched — SHAPE still depends
 * ONLY on `vehicleType`. The colour axis gains one input beyond tone, and that is
 * the whole of the change.
 *
 * 🔴 RED MEANS ANY OPEN OVERSPEED EPISODE (Mahad, 21 Sep: "yes red for any
 * overspeed, keep it simple"). Severity — info/warning/critical — does NOT modulate
 * this. A vehicle 3 km/h over is the same red as one 40 km/h over. Severity still
 * grades the ALERT FEED; it must never be wired into marker colour. If you are here
 * to "improve" this by grading it, that is a product change and it is Mahad's call,
 * not a refactor.
 *
 * NOTE the previous revision of this comment claimed a fifth paint class "would be a
 * compile error in `paintClassForTone`". It would not have been — that function has a
 * `default:` arm, so a new member compiles silently. CLAUDE.md forbids documenting a
 * guard that does not execute, so the false claim is removed rather than reworded.
 * The real exhaustiveness guard is `MARKER_PAINT_CLASSES` + the atlas full-matrix test.
 */
export type MarkerPaintClass = 'moving' | 'idling' | 'still' | 'offline' | 'speeding';

export const MARKER_PAINT_CLASSES: readonly MarkerPaintClass[] = [
  'moving',
  'idling',
  'still',
  'offline',
  'speeding',
] as const;

/** The tone → paint-class collapse. `stopped`/`stale`/`unknown` → `still`; `offline`
 *  keeps its own class (its own grey, filled). This is the whole map language.
 *
 *  🔴 This function NEVER returns `speeding`, and that is by design, not an omission:
 *  `speeding` is not a tone, so it is not derivable from one. `toFeature` overlays it
 *  on top of this result. Keep it that way — folding a breach flag in here would put
 *  a non-motion fact into the motion axis. */
export function paintClassForTone(tone: MarkerTone): MarkerPaintClass {
  switch (tone) {
    case 'moving':
      return 'moving';
    case 'idle':
      return 'idling';
    case 'offline':
      return 'offline';
    default:
      // stopped, stale, unknown — one grey, one image.
      return 'still';
  }
}

/** Neutral marker tokens — glass and keyline carry NO state meaning (colour is the
 *  motion axis alone), so they are their own neutral tokens, not any tone's colour. */
export const MARKER_GLASS_TOKEN = '--raad-marker-glass';
export const MARKER_KEYLINE_TOKEN = '--raad-marker-keyline';

/** The speeding body colour. 🔴 Reuses the EXISTING `--raad-error`, which already
 *  ships in both the light and dark palettes — no new token is minted for this.
 *  Measured against the other map fills at CIEDE2000: the tightest pair is
 *  amber↔red at 27.0 (light) / 33.3 (dark), against a floor of 6.0, so the four
 *  map fills stay trivially separable. `markerSeparability.test.ts` carries this
 *  token in `MAP_TONE_TOKENS` so the guard actually covers red — a guard that
 *  passed while not testing the new colour would be mechanism, not outcome. */
export const MARKER_SPEEDING_TOKEN = '--raad-error';

export interface ResolvedIcon {
  /** Depends ONLY on vehicle type. */
  glyph: Glyph;
  /** The CSS custom-property NAME for the body colour — depends ONLY on tone. */
  colorToken: string;
}

/**
 * THE two-axis ruling as one pure function. `glyph` is a function of `type` alone;
 * `colorToken` is a function of `tone` alone. The two inputs never cross — that is
 * exactly the property the paired axis tests pin (same type / every tone → one
 * glyph, many tokens; same tone / every type → one token, many glyphs).
 */
export function resolveIcon(type: VehicleType, tone: MarkerTone): ResolvedIcon {
  return { glyph: glyphForType(type), colorToken: TONE_COLOR_TOKEN[tone] };
}

/* ── Runtime colour resolution (getComputedStyle), mirroring trail.ts ───────── */

export interface MarkerColors {
  /** Resolved body/dot colour per tone. Since 56c the four not-moving tones resolve
   *  to the SAME `--raad-marker-still` grey — the collapse lives in TONE_COLOR_TOKEN. */
  tone: Record<MarkerTone, string>;
  /** Neutral dark-slate windscreen fill. */
  glass: string;
  /** Neutral white keyline/halo. */
  keyline: string;
  /** Resolved `--raad-error` — the body fill for the `speeding` paint class. */
  speeding: string;
}

/**
 * Resolve every marker colour from a property reader. The runtime reader is
 * `(n) => getComputedStyle(document.documentElement).getPropertyValue(n)`; tests
 * feed a fake reader with known values and assert the layers/atlas carry exactly
 * those — so "the resolved value equals the token" is an executed outcome, not a
 * claim. No colour is written here; the tokens live in styles.css alone.
 */
export function resolveMarkerColors(read: (name: string) => string): MarkerColors {
  const tone = {} as Record<MarkerTone, string>;
  for (const t of MARKER_TONES) tone[t] = read(TONE_COLOR_TOKEN[t]).trim();
  return {
    tone,
    glass: read(MARKER_GLASS_TOKEN).trim(),
    keyline: read(MARKER_KEYLINE_TOKEN).trim(),
    speeding: read(MARKER_SPEEDING_TOKEN).trim(),
  };
}

/**
 * The fill/glass/keyline colours a rasterised silhouette (or dot) needs for a PAINT
 * CLASS. 🔴 EVERY CLASS IS SOLID — body carries the state colour, glass and keyline
 * stay neutral. `offline` used to return a transparent body AND glass with the grey
 * as the keyline, so "no contact" drew as an empty outline; it was invisible on
 * satellite and is gone (Mahad, 21 Sep: no hollow at all).
 *
 * 🔴 `offline` MUST BE NAMED EXPLICITLY below. It is tempting to delete its branch
 * now that it is no longer special — but the `else` returns `colors.tone.stopped`,
 * so deleting it silently paints a dead tracker in the PARKED grey, which is exactly
 * the collision the two-grey split exists to prevent. A test asserts the two are not
 * equal; it is the only thing standing between that tidy-up and a wrong marker.
 */
export function colorsForPaintClass(pc: MarkerPaintClass, colors: MarkerColors): GlyphColors {
  // Speeding keeps the MOVING silhouette (rotated to course, so no heading
  // information is lost) and swaps only the body fill to the error red. Red
  // therefore reads as "moving AND breaching", never as a state of its own.
  const body =
    pc === 'speeding'
      ? colors.speeding
      : pc === 'moving'
        ? colors.tone.moving
        : pc === 'idling'
          ? colors.tone.idle
          : pc === 'offline'
            ? colors.tone.offline
            : colors.tone.stopped;
  return { body, glass: colors.glass, keyline: colors.keyline };
}

/**
 * The DOT circle layer's fill expression, keyed on the precomputed `paintClass` — so
 * the dot (now only the `other`/unclassified case) paints exactly the four map
 * states: moving green, idling amber, parked slate and no-contact's own grey — all
 * SOLID. No hex; the same collapse the silhouettes use.
 */
export function dotColorExpression(colors: MarkerColors): ExpressionSpecification {
  return [
    'match',
    ['get', 'paintClass'],
    'moving', colors.tone.moving,
    'idling', colors.tone.idle,
    'offline', colors.tone.offline,
    // still — stopped/stale/unknown all here.
    colors.tone.stopped,
  ];
}

/** The DOT circle layer's stroke expression. EVERY class takes the white keyline —
 *  offline included, since it is filled now. The `offline` branch is kept only so the
 *  `match` retains a labelled branch and is IDENTICAL to the fallback; do not read it
 *  as evidence that offline is still special. */
export function dotStrokeExpression(colors: MarkerColors): ExpressionSpecification {
  return [
    'match',
    ['get', 'paintClass'],
    'offline', colors.keyline,
    colors.keyline,
  ];
}

/* ── Size class: which form renders, and at what on-screen size ─────────────── */

/**
 * Marker on-screen size is a function of ZOOM ONLY — never of tone. A parked
 * vehicle is drawn the SAME size as a moving one; shrinking it would read as
 * meaningful to a customer (Mahad). Markers are ~40 px full at normal viewing zoom;
 * they shrink toward the simplified `sm` glyph only when zoomed out (or, later,
 * clustered). These constants define that ramp and the split; every derived value —
 * the `icon-size` interpolation, the layer zoom split, the size-class selection —
 * comes from them, so they cannot drift apart.
 */
export const ICON_MIN_ZOOM = 8;
export const ICON_MAX_ZOOM = 17;
/** Rendered marker height (CSS px) at the two zoom ends — ~40 px full at normal
 *  zoom, dropping below the split only when zoomed well out. */
export const PX_AT_MIN_ZOOM = 22;
export const PX_AT_MAX_ZOOM = 44;
/** The image is rasterised at this height (CSS px, × devicePixelRatio); icon-size
 *  scales it to the rendered px above. */
export const ICON_BASE_PX = 40;
/** Below this rendered size → simplified `sm`; at/above → `full`. Slice 58: the
 *  markers-v2 handoff draws its `-small` forms for 24–39 px and its detailed
 *  `-large` forms for ≥ 40 px, so the split follows the designer (was 30). */
export const SIZE_SPLIT_PX = 40;

/** The rendered marker height (CSS px) at a given zoom — clamped linear ramp. */
export function renderedPxAtZoom(zoom: number): number {
  const z = Math.min(ICON_MAX_ZOOM, Math.max(ICON_MIN_ZOOM, zoom));
  const frac = (z - ICON_MIN_ZOOM) / (ICON_MAX_ZOOM - ICON_MIN_ZOOM);
  return PX_AT_MIN_ZOOM + frac * (PX_AT_MAX_ZOOM - PX_AT_MIN_ZOOM);
}

/** Size class from a rendered pixel size — the design's swap rule made explicit. */
export function sizeClassForRenderedPx(px: number): 'full' | 'sm' {
  return px < SIZE_SPLIT_PX ? 'sm' : 'full';
}

/** Size class at a zoom — the composition the two symbol layers split on. */
export function sizeClassForZoom(zoom: number): 'full' | 'sm' {
  return sizeClassForRenderedPx(renderedPxAtZoom(zoom));
}

/** The zoom at which the ramp crosses SIZE_SPLIT_PX — the layers' minzoom/maxzoom
 *  boundary. Below it the `sm` layer draws; at/above, the `full` layer. */
export const SPLIT_ZOOM =
  ICON_MIN_ZOOM +
  ((SIZE_SPLIT_PX - PX_AT_MIN_ZOOM) / (PX_AT_MAX_ZOOM - PX_AT_MIN_ZOOM)) *
    (ICON_MAX_ZOOM - ICON_MIN_ZOOM);

/** The map image id for a glyph + PAINT CLASS + form. The paint class is in the id
 *  because the SAME silhouette renders in the four map colours (a still car is a grey
 *  car, a moving car a green one, an offline car one in its own grey), so each is its own
 *  image. Since 56c the key is the paint class, not the tone — stopped/stale/unknown
 *  share the one `still` image. */
export function silhouetteImageId(
  glyph: Exclude<Glyph, 'dot'>,
  paintClass: MarkerPaintClass,
  form: 'full' | 'sm',
): string {
  return `veh-${glyph}-${paintClass}-${form}`;
}

/**
 * The re-supply matcher: parse a silhouette image id back to its glyph + paint class
 * + form, so `styleimagemissing` can re-rasterise exactly the one image a carried-over
 * symbol layer asks for after a setStyle swap clears images. 🔴 IT MUST ADMIT EVERY
 * glyph the atlas produces — `threewheel` included (a slice-55 omission here left the
 * Bajaj un-re-supplied after a basemap swap). `parseSilhouetteId` round-trips every
 * `silhouetteImageEntries` id; the test that drives that IS the gate arm.
 */
export const SILHOUETTE_ID_RE =
  /^veh-(sedan|suv|landcruiser|pickup|minibus|truck|watertanker|bus|motorbike|generic|threewheel)-(moving|idling|still|offline|speeding)-(full|sm)$/;

export interface ParsedSilhouetteId {
  glyph: Exclude<Glyph, 'dot'>;
  paintClass: MarkerPaintClass;
  form: 'full' | 'sm';
}

/** Parse a silhouette image id, or null if it is not one (e.g. the name-tag pill). */
export function parseSilhouetteId(id: string): ParsedSilhouetteId | null {
  const m = SILHOUETTE_ID_RE.exec(id);
  if (!m) return null;
  return {
    glyph: m[1] as Exclude<Glyph, 'dot'>,
    paintClass: m[2] as MarkerPaintClass,
    form: m[3] as 'full' | 'sm',
  };
}

/** The image id a vehicle selects at `zoom` — the RENDERED selection, size class
 *  and all. Ties the glyph/form choice to actual rendered px. */
export function silhouetteImageIdForZoom(
  glyph: Exclude<Glyph, 'dot'>,
  paintClass: MarkerPaintClass,
  zoom: number,
): string {
  return silhouetteImageId(glyph, paintClass, sizeClassForZoom(zoom));
}

/** `icon-size` interpolation shared by both symbol layers, derived from the ramp
 *  constants so the rendered px matches `renderedPxAtZoom` exactly. Depends on
 *  ZOOM ONLY — never tone. */
export function iconSizeExpression(): ExpressionSpecification {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    ICON_MIN_ZOOM, PX_AT_MIN_ZOOM / ICON_BASE_PX,
    ICON_MAX_ZOOM, PX_AT_MAX_ZOOM / ICON_BASE_PX,
  ];
}

/** `icon-image` for a size form: `veh-<iconGlyph>-<paintClass>-<form>`, selected per
 *  feature. The silhouette layers are filtered so `iconGlyph` is never `dot`, and
 *  `paintClass` is always one of MARKER_PAINT_CLASSES (an image we generated). */
export function iconImageExpression(form: 'full' | 'sm'): ExpressionSpecification {
  return ['concat', 'veh-', ['get', 'iconGlyph'], '-', ['get', 'paintClass'], `-${form}`];
}

/* ── Geometry (verbatim) + the rasteriser ──────────────────────────────────── */

export const GLYPH_VIEWBOX = { w: 40, h: 72 } as const;

type FillRole = 'body' | 'glass' | 'keyline';

interface DrawOp {
  /** SVG path `d` (verbatim from the reference), OR a rounded rect / circle. */
  d?: string;
  rect?: readonly [x: number, y: number, w: number, h: number, r: number];
  circle?: readonly [cx: number, cy: number, r: number];
  fill?: FillRole;
  /** A keyline stroke, painted BEHIND the fill (SVG paint-order:stroke) so it
   *  reads as an outer halo. */
  stroke?: FillRole;
  strokeWidth?: number;
  fillRule?: 'evenodd';
}

interface GlyphShape {
  full: readonly DrawOp[];
  sm: readonly DrawOp[];
}

const GLYPHS: Record<Glyph, GlyphShape> = {
  // BEGIN GENERATED vehicle glyphs (ci/gen-vehicle-glyphs.mjs — do not edit by hand)
  sedan: {
    full: [
      { d: 'M20 2.5 C26 2.5 30.5 3.4 32 6.5 C33.2 9 33.4 13 33.4 17 C33.4 24 33 28 32.9 34 C32.8 42 33.3 50 33.3 56 C33.3 63 32.9 66.5 31 68 C28.5 69.6 24 69.8 20 69.8 C16 69.8 11.5 69.6 9 68 C7.1 66.5 6.7 63 6.7 56 C6.7 50 7.2 42 7.1 34 C7 28 6.6 24 6.6 17 C6.6 13 6.8 9 8 6.5 C9.5 3.4 14 2.5 20 2.5 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M7.4 23.8 C5.9 24 4.3 24.9 3.9 26.1 C3.7 26.9 4.4 27.3 5.5 27.2 L7.4 27 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M32.6 23.8 C34.1 24 35.7 24.9 36.1 26.1 C36.3 26.9 35.6 27.3 34.5 27.2 L32.6 27 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M10 21.2 C13 18.6 16.5 17.4 20 17.4 C23.5 17.4 27 18.6 30 21.2 C29.4 24.5 28.8 27.5 28.2 30.6 C25.5 30.1 22.8 29.9 20 29.9 C17.2 29.9 14.5 30.1 11.8 30.6 C11.2 27.5 10.6 24.5 10 21.2 Z', fill: 'glass' },
      { d: 'M9.4 26 C10 28 10.8 30 11.3 31.6 L11.3 39.6 L9 40 C8.9 35 9 30 9.4 26 Z', fill: 'glass' },
      { d: 'M30.6 26 C30 28 29.2 30 28.7 31.6 L28.7 39.6 L31 40 C31.1 35 31 30 30.6 26 Z', fill: 'glass' },
      { d: 'M9 41.4 L11.3 41 L11.4 51 C10.6 53 9.8 54.6 9.1 55.8 C8.9 51 8.9 46 9 41.4 Z', fill: 'glass' },
      { d: 'M31 41.4 L28.7 41 L28.6 51 C29.4 53 30.2 54.6 30.9 55.8 C31.1 51 31.1 46 31 41.4 Z', fill: 'glass' },
      { d: 'M12 51.8 C14.6 51.3 17.3 51.1 20 51.1 C22.7 51.1 25.4 51.3 28 51.8 C28.6 54.8 29.2 57.8 29.6 60.4 C26.5 61.6 23.3 62.1 20 62.1 C16.7 62.1 13.5 61.6 10.4 60.4 C10.8 57.8 11.4 54.8 12 51.8 Z', fill: 'glass' },
      { d: 'M8.6 7.6 C9.8 5.2 12 4 14.6 3.6 C12.4 4.8 10.6 6.6 9.6 9 Z', fill: 'keyline' },
      { d: 'M31.4 7.6 C30.2 5.2 28 4 25.4 3.6 C27.6 4.8 29.4 6.6 30.4 9 Z', fill: 'keyline' },
      { d: 'M7.6 63.4 C7.8 66 8.9 67.6 11.4 68.4 C9.8 67 8.8 65.4 8.4 63.2 Z', fill: 'keyline' },
      { d: 'M32.4 63.4 C32.2 66 31.1 67.6 28.6 68.4 C30.2 67 31.2 65.4 31.6 63.2 Z', fill: 'keyline' },
    ],
    sm: [
      { d: 'M20 2.5 C26 2.5 30.5 3.4 32 6.5 C33.2 9 33.4 13 33.4 17 C33.4 24 33 28 32.9 34 C32.8 42 33.3 50 33.3 56 C33.3 63 32.9 66.5 31 68 C28.5 69.6 24 69.8 20 69.8 C16 69.8 11.5 69.6 9 68 C7.1 66.5 6.7 63 6.7 56 C6.7 50 7.2 42 7.1 34 C7 28 6.6 24 6.6 17 C6.6 13 6.8 9 8 6.5 C9.5 3.4 14 2.5 20 2.5 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M7.4 23.8 C5.9 24 4.3 24.9 3.9 26.1 C3.7 26.9 4.4 27.3 5.5 27.2 L7.4 27 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M32.6 23.8 C34.1 24 35.7 24.9 36.1 26.1 C36.3 26.9 35.6 27.3 34.5 27.2 L32.6 27 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M9.6 21 C12.8 18.2 16.4 16.9 20 16.9 C23.6 16.9 27.2 18.2 30.4 21 C29.8 24.5 29.1 27.8 28.5 31 C25.7 30.5 22.9 30.3 20 30.3 C17.1 30.3 14.3 30.5 11.5 31 C10.9 27.8 10.2 24.5 9.6 21 Z', fill: 'glass' },
      { d: 'M11.6 51.4 C14.4 50.9 17.2 50.7 20 50.7 C22.8 50.7 25.6 50.9 28.4 51.4 C29 54.6 29.6 57.8 30 60.6 C26.7 61.9 23.4 62.5 20 62.5 C16.6 62.5 13.3 61.9 10 60.6 C10.4 57.8 11 54.6 11.6 51.4 Z', fill: 'glass' },
    ],
  },
  suv: {
    full: [
      { rect: [4.1, 10.4, 3.4, 11.2, 1.2], fill: 'glass' },
      { rect: [32.5, 10.4, 3.4, 11.2, 1.2], fill: 'glass' },
      { rect: [4.1, 49, 3.4, 11.2, 1.2], fill: 'glass' },
      { rect: [32.5, 49, 3.4, 11.2, 1.2], fill: 'glass' },
      { d: 'M20 3 C27 3 31.8 3.4 33 6.5 Q33.8 9 33.8 14 L33.8 62 Q33.8 67.5 31 68.6 Q26 69.3 20 69.3 Q14 69.3 9 68.6 Q6.2 67.5 6.2 62 L6.2 14 Q6.2 9 7 6.5 C8.2 3.4 13 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 23.6 C5.2 23.8 3.6 24.7 3.2 25.9 C3 26.7 3.7 27.1 4.8 27 L6.7 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 23.6 C34.8 23.8 36.4 24.7 36.8 25.9 C37 26.7 36.3 27.1 35.2 27 L33.3 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 19.5 C12 17.4 16 16.8 20 16.8 C24 16.8 28 17.4 31.2 19.5 C30.2 23.5 29.2 27.5 28.2 31 C25.5 30.4 22.8 30.2 20 30.2 C17.2 30.2 14.5 30.4 11.8 31 C10.8 27.5 9.8 23.5 8.8 19.5 Z', fill: 'glass' },
      { rect: [14, 34, 12, 15.6, 1.6], fill: 'glass' },
      { d: 'M8.2 26.5 L10.4 30.4 L10.6 38.4 L8.1 38.6 Z', fill: 'glass' },
      { d: 'M8.1 40.4 L10.6 40.4 L10.6 49.8 L8.1 49.8 Z', fill: 'glass' },
      { d: 'M8.1 51.8 L10.6 51.8 L10.4 58.6 L8.2 60.6 Z', fill: 'glass' },
      { d: 'M31.8 26.5 L29.6 30.4 L29.4 38.4 L31.9 38.6 Z', fill: 'glass' },
      { d: 'M31.9 40.4 L29.4 40.4 L29.4 49.8 L31.9 49.8 Z', fill: 'glass' },
      { d: 'M31.9 51.8 L29.4 51.8 L29.6 58.6 L31.8 60.6 Z', fill: 'glass' },
      { d: 'M10.4 62 C13.5 61.2 16.8 60.9 20 60.9 C23.2 60.9 26.5 61.2 29.6 62 L29 66 C26 66.9 23 67.2 20 67.2 C17 67.2 14 66.9 11 66 Z', fill: 'glass' },
      { d: 'M7.4 7.2 C8.4 5 10.6 3.9 13.4 3.6 C11.2 4.8 9.4 6.6 8.3 9 Z', fill: 'keyline' },
      { d: 'M32.6 7.2 C31.6 5 29.4 3.9 26.6 3.6 C28.8 4.8 30.6 6.6 31.7 9 Z', fill: 'keyline' },
      { d: 'M6.9 62.6 C7 65.6 8 67.4 10.4 68.2 C8.9 66.8 8 65 7.7 62.4 Z', fill: 'keyline' },
      { d: 'M33.1 62.6 C33 65.6 32 67.4 29.6 68.2 C31.1 66.8 32 65 32.3 62.4 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [4.1, 10.4, 3.4, 11.2, 1.2], fill: 'glass' },
      { rect: [32.5, 10.4, 3.4, 11.2, 1.2], fill: 'glass' },
      { rect: [4.1, 49, 3.4, 11.2, 1.2], fill: 'glass' },
      { rect: [32.5, 49, 3.4, 11.2, 1.2], fill: 'glass' },
      { d: 'M20 3 C27 3 31.8 3.4 33 6.5 Q33.8 9 33.8 14 L33.8 62 Q33.8 67.5 31 68.6 Q26 69.3 20 69.3 Q14 69.3 9 68.6 Q6.2 67.5 6.2 62 L6.2 14 Q6.2 9 7 6.5 C8.2 3.4 13 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 23.6 C5.2 23.8 3.6 24.7 3.2 25.9 C3 26.7 3.7 27.1 4.8 27 L6.7 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 23.6 C34.8 23.8 36.4 24.7 36.8 25.9 C37 26.7 36.3 27.1 35.2 27 L33.3 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.6 19.3 C11.9 17.1 16 16.4 20 16.4 C24 16.4 28.1 17.1 31.4 19.3 C30.4 23.4 29.4 27.6 28.4 31.4 C25.6 30.8 22.8 30.6 20 30.6 C17.2 30.6 14.4 30.8 11.6 31.4 C10.6 27.6 9.6 23.4 8.6 19.3 Z', fill: 'glass' },
      { d: 'M10 61.6 C13.3 60.7 16.6 60.4 20 60.4 C23.4 60.4 26.7 60.7 30 61.6 L29.3 66.2 C26.2 67.2 23.1 67.5 20 67.5 C16.9 67.5 13.8 67.2 10.7 66.2 Z', fill: 'glass' },
    ],
  },
  landcruiser: {
    full: [
      { rect: [3.7, 10.8, 3.8, 11, 1.2], fill: 'glass' },
      { rect: [32.5, 10.8, 3.8, 11, 1.2], fill: 'glass' },
      { rect: [3.4, 49.6, 3.6, 11, 1.2], fill: 'glass' },
      { rect: [33, 49.6, 3.6, 11, 1.2], fill: 'glass' },
      { d: 'M12.5 3 L27.5 3 Q32 3 33 5 L33.8 8 L33.8 17 Q34.6 19 34.6 22 L34.6 63 Q34.6 68.6 30.5 69.2 L9.5 69.2 Q5.4 68.6 5.4 63 L5.4 22 Q5.4 19 6.2 17 L6.2 8 L7 5 Q8 3 12.5 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6 22.4 C4.5 22.6 3 23.4 2.6 24.7 C2.4 25.6 3.1 26 4.3 25.9 L6 25.7 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M34 22.4 C35.5 22.6 37 23.4 37.4 24.7 C37.6 25.6 36.9 26 35.7 25.9 L34 25.7 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.6 20.4 C12.3 19 16.2 18.5 20 18.5 C23.8 18.5 27.7 19 31.4 20.4 L29.6 32 C26.4 31.6 23.2 31.5 20 31.5 C16.8 31.5 13.6 31.6 10.4 32 Z', fill: 'glass' },
      { rect: [15.6, 35, 8.8, 8, 1], fill: 'glass' },
      { d: 'M7.6 25.6 L9.6 29.2 L9.6 40 L7.6 40 Z', fill: 'glass' },
      { d: 'M7.6 41.2 L9.6 41.2 L9.6 51.2 L7.6 51.2 Z', fill: 'glass' },
      { d: 'M7.6 52.4 L9.6 52.4 L9.6 58.4 L7.6 59.6 Z', fill: 'glass' },
      { d: 'M32.4 25.6 L30.4 29.2 L30.4 40 L32.4 40 Z', fill: 'glass' },
      { d: 'M32.4 41.2 L30.4 41.2 L30.4 51.2 L32.4 51.2 Z', fill: 'glass' },
      { d: 'M32.4 52.4 L30.4 52.4 L30.4 58.4 L32.4 59.6 Z', fill: 'glass' },
      { d: 'M10.2 64.2 L29.8 64.2 L29.2 67.4 L10.8 67.4 Z', fill: 'glass' },
      { d: 'M7.4 6 L13 4.3 L13 5.4 L7.8 7.2 Z', fill: 'keyline' },
      { d: 'M32.6 6 L27 4.3 L27 5.4 L32.2 7.2 Z', fill: 'keyline' },
      { d: 'M6.5 60.6 L7.4 60.6 L7.4 67.6 L6.5 66.6 Z', fill: 'keyline' },
      { d: 'M33.5 60.6 L32.6 60.6 L32.6 67.6 L33.5 66.6 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [3.7, 10.8, 3.8, 11, 1.2], fill: 'glass' },
      { rect: [32.5, 10.8, 3.8, 11, 1.2], fill: 'glass' },
      { rect: [3.4, 49.6, 3.6, 11, 1.2], fill: 'glass' },
      { rect: [33, 49.6, 3.6, 11, 1.2], fill: 'glass' },
      { d: 'M12.5 3 L27.5 3 Q32 3 33 5 L33.8 8 L33.8 17 Q34.6 19 34.6 22 L34.6 63 Q34.6 68.6 30.5 69.2 L9.5 69.2 Q5.4 68.6 5.4 63 L5.4 22 Q5.4 19 6.2 17 L6.2 8 L7 5 Q8 3 12.5 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6 22.4 C4.5 22.6 3 23.4 2.6 24.7 C2.4 25.6 3.1 26 4.3 25.9 L6 25.7 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M34 22.4 C35.5 22.6 37 23.4 37.4 24.7 C37.6 25.6 36.9 26 35.7 25.9 L34 25.7 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.4 20.2 C12.2 18.7 16.1 18.2 20 18.2 C23.9 18.2 27.8 18.7 31.6 20.2 L29.8 32.4 C26.5 32 23.3 31.9 20 31.9 C16.7 31.9 13.5 32 10.2 32.4 Z', fill: 'glass' },
      { d: 'M9.8 63.6 L30.2 63.6 L29.4 67.6 L10.6 67.6 Z', fill: 'glass' },
    ],
  },
  pickup: {
    full: [
      { rect: [4.2, 10.6, 3.4, 11, 1.2], fill: 'glass' },
      { rect: [32.4, 10.6, 3.4, 11, 1.2], fill: 'glass' },
      { rect: [4.2, 50, 3.4, 11, 1.2], fill: 'glass' },
      { rect: [32.4, 50, 3.4, 11, 1.2], fill: 'glass' },
      { d: 'M20 3 C27 3 31.6 3.4 32.9 6 Q33.8 8.4 33.8 13 L33.8 64.5 Q33.8 68.8 30 69.2 L10 69.2 Q6.2 68.8 6.2 64.5 L6.2 13 Q6.2 8.4 7.1 6 C8.4 3.4 13 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 23.6 C5.2 23.8 3.6 24.700000000000003 3.2 25.900000000000002 C3 26.700000000000003 3.7 27.1 4.8 27 L6.7 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 23.6 C34.8 23.8 36.4 24.700000000000003 36.8 25.900000000000002 C37 26.700000000000003 36.3 27.1 35.2 27 L33.3 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 19.5 C12 17.6 16 17 20 17 C24 17 28 17.6 31.2 19.5 L29.6 29 C26.4 28.5 23.2 28.3 20 28.3 C16.8 28.3 13.6 28.5 10.4 29 Z', fill: 'glass' },
      { rect: [9, 42.2, 22, 24.6, 1], fill: 'glass' },
      { d: 'M8.2 26.5 L10.2 29.8 L10.2 37 L8.2 37 Z', fill: 'glass' },
      { d: 'M31.8 26.5 L29.8 29.8 L29.8 37 L31.8 37 Z', fill: 'glass' },
      { d: 'M10.6 37.4 L29.4 37.4 L29 39.4 L11 39.4 Z', fill: 'glass' },
      { d: 'M9 50.4 L31 50.4', stroke: 'keyline', strokeWidth: 0.7 },
      { d: 'M9 58.6 L31 58.6', stroke: 'keyline', strokeWidth: 0.7 },
      { d: 'M7.4 7.2 C8.4 5 10.6 3.9 13.4 3.6 C11.2 4.8 9.4 6.6 8.3 9 Z', fill: 'keyline' },
      { d: 'M32.6 7.2 C31.6 5 29.4 3.9 26.6 3.6 C28.8 4.8 30.6 6.6 31.7 9 Z', fill: 'keyline' },
      { d: 'M6.9 63 C7 66 7.8 67.8 9.8 68.4 C8.6 67 7.9 65.2 7.7 62.8 Z', fill: 'keyline' },
      { d: 'M33.1 63 C33 66 32.2 67.8 30.2 68.4 C31.4 67 32.1 65.2 32.3 62.8 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [4.2, 10.6, 3.4, 11, 1.2], fill: 'glass' },
      { rect: [32.4, 10.6, 3.4, 11, 1.2], fill: 'glass' },
      { rect: [4.2, 50, 3.4, 11, 1.2], fill: 'glass' },
      { rect: [32.4, 50, 3.4, 11, 1.2], fill: 'glass' },
      { d: 'M20 3 C27 3 31.6 3.4 32.9 6 Q33.8 8.4 33.8 13 L33.8 64.5 Q33.8 68.8 30 69.2 L10 69.2 Q6.2 68.8 6.2 64.5 L6.2 13 Q6.2 8.4 7.1 6 C8.4 3.4 13 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 23.6 C5.2 23.8 3.6 24.700000000000003 3.2 25.900000000000002 C3 26.700000000000003 3.7 27.1 4.8 27 L6.7 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 23.6 C34.8 23.8 36.4 24.700000000000003 36.8 25.900000000000002 C37 26.700000000000003 36.3 27.1 35.2 27 L33.3 26.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 19.5 C12 17.6 16 17 20 17 C24 17 28 17.6 31.2 19.5 L29.6 29 C26.4 28.5 23.2 28.3 20 28.3 C16.8 28.3 13.6 28.5 10.4 29 Z', fill: 'glass' },
      { rect: [9, 42.2, 22, 24.6, 1], fill: 'glass' },
    ],
  },
  minibus: {
    full: [
      { rect: [4.4, 8.6, 3.2, 10.4, 1.2], fill: 'glass' },
      { rect: [32.4, 8.6, 3.2, 10.4, 1.2], fill: 'glass' },
      { rect: [4.4, 52, 3.2, 10.4, 1.2], fill: 'glass' },
      { rect: [32.4, 52, 3.2, 10.4, 1.2], fill: 'glass' },
      { d: 'M20 3 C27.5 3 31.8 3.6 33 6.4 Q33.6 8.4 33.6 11.5 L33.6 66 Q33.6 69.5 30.6 69.5 L9.4 69.5 Q6.4 69.5 6.4 66 L6.4 11.5 Q6.4 8.4 7 6.4 C8.2 3.6 12.5 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 10.2 C5.2 10.399999999999999 3.6 11.299999999999999 3.2 12.5 C3 13.299999999999999 3.7 13.7 4.8 13.6 L6.7 13.399999999999999 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 10.2 C34.8 10.399999999999999 36.4 11.299999999999999 36.8 12.5 C37 13.299999999999999 36.3 13.7 35.2 13.6 L33.3 13.399999999999999 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 9.6 C12.4 8 16.2 7.4 20 7.4 C23.8 7.4 27.6 8 31.2 9.6 L30.2 16.4 C26.8 15.9 23.4 15.7 20 15.7 C16.6 15.7 13.2 15.9 9.8 16.4 Z', fill: 'glass' },
      { d: 'M10.4 63.8 L29.6 63.8 Q30 63.8 29.9 64.2 L29.5 66.8 Q29.4 67.2 29 67.2 L11 67.2 Q10.6 67.2 10.5 66.8 L10.1 64.2 Q10 63.8 10.4 63.8 Z', fill: 'glass' },
      { d: 'M7.8 18.4 L9.6 18.4 L9.6 28 L7.8 28 Z', fill: 'glass' },
      { d: 'M32.2 18.4 L30.4 18.4 L30.4 28 L32.2 28 Z', fill: 'glass' },
      { d: 'M7.8 29.4 L9.6 29.4 L9.6 39 L7.8 39 Z', fill: 'glass' },
      { d: 'M32.2 29.4 L30.4 29.4 L30.4 39 L32.2 39 Z', fill: 'glass' },
      { d: 'M7.8 40.4 L9.6 40.4 L9.6 50 L7.8 50 Z', fill: 'glass' },
      { d: 'M32.2 40.4 L30.4 40.4 L30.4 50 L32.2 50 Z', fill: 'glass' },
      { d: 'M7.8 51.4 L9.6 51.4 L9.6 61 L7.8 61 Z', fill: 'glass' },
      { d: 'M32.2 51.4 L30.4 51.4 L30.4 61 L32.2 61 Z', fill: 'glass' },
      { d: 'M12 20 L28 20', stroke: 'keyline', strokeWidth: 0.6 },
      { d: 'M7.6 6.6 C8.6 4.8 10.6 3.9 13.2 3.6 C11.2 4.6 9.6 5.9 8.5 7.8 Z', fill: 'keyline' },
      { d: 'M32.4 6.6 C31.4 4.8 29.4 3.9 26.8 3.6 C28.8 4.6 30.4 5.9 31.5 7.8 Z', fill: 'keyline' },
      { d: 'M7.2 64 L8.2 64 L8.2 68.4 Q7.2 68 7.2 67 Z', fill: 'keyline' },
      { d: 'M32.8 64 L31.8 64 L31.8 68.4 Q32.8 68 32.8 67 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [4.4, 8.6, 3.2, 10.4, 1.2], fill: 'glass' },
      { rect: [32.4, 8.6, 3.2, 10.4, 1.2], fill: 'glass' },
      { rect: [4.4, 52, 3.2, 10.4, 1.2], fill: 'glass' },
      { rect: [32.4, 52, 3.2, 10.4, 1.2], fill: 'glass' },
      { d: 'M20 3 C27.5 3 31.8 3.6 33 6.4 Q33.6 8.4 33.6 11.5 L33.6 66 Q33.6 69.5 30.6 69.5 L9.4 69.5 Q6.4 69.5 6.4 66 L6.4 11.5 Q6.4 8.4 7 6.4 C8.2 3.6 12.5 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 10.2 C5.2 10.399999999999999 3.6 11.299999999999999 3.2 12.5 C3 13.299999999999999 3.7 13.7 4.8 13.6 L6.7 13.399999999999999 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 10.2 C34.8 10.399999999999999 36.4 11.299999999999999 36.8 12.5 C37 13.299999999999999 36.3 13.7 35.2 13.6 L33.3 13.399999999999999 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 9.6 C12.4 8 16.2 7.4 20 7.4 C23.8 7.4 27.6 8 31.2 9.6 L30.2 16.4 C26.8 15.9 23.4 15.7 20 15.7 C16.6 15.7 13.2 15.9 9.8 16.4 Z', fill: 'glass' },
      { d: 'M10.4 63.8 L29.6 63.8 Q30 63.8 29.9 64.2 L29.5 66.8 Q29.4 67.2 29 67.2 L11 67.2 Q10.6 67.2 10.5 66.8 L10.1 64.2 Q10 63.8 10.4 63.8 Z', fill: 'glass' },
    ],
  },
  truck: {
    full: [
      { rect: [5.6, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [31.4, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [3.2, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [33.8, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [3.2, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [33.8, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { d: 'M7 20.2 L33 20.2 Q34.4 20.2 34.4 21.6 L34.4 67.8 Q34.4 69.2 33 69.2 L7 69.2 Q5.6 69.2 5.6 67.8 L5.6 21.6 Q5.6 20.2 7 20.2 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M20 3 C26.5 3 30.5 3.3 31.5 5 Q32 6.4 32 8.5 L32 17 Q32 18.4 30.6 18.4 L9.4 18.4 Q8 18.4 8 17 L8 8.5 Q8 6.4 8.5 5 C9.5 3.3 13.5 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M8 6.2 L4.8 5.8 Q4 5.8 4 6.6 L4 9.6 Q4 10.2 4.8 10.2 L8 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M32 6.2 L35.2 5.8 Q36 5.8 36 6.6 L36 9.6 Q36 10.2 35.2 10.2 L32 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M10 5.8 Q20 4.2 30 5.8 L29.4 9.8 Q20 8.8 10.6 9.8 Z', fill: 'glass' },
      { d: 'M7.4 32 L32.6 32', stroke: 'keyline', strokeWidth: 0.7 },
      { d: 'M7.4 44 L32.6 44', stroke: 'keyline', strokeWidth: 0.7 },
      { d: 'M7.4 56 L32.6 56', stroke: 'keyline', strokeWidth: 0.7 },
      { d: 'M9 4.8 L13.6 3.6 L13.6 4.4 L9.3 5.7 Z', fill: 'keyline' },
      { d: 'M31 4.8 L26.4 3.6 L26.4 4.4 L30.7 5.7 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [5.6, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [31.4, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [3.2, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [33.8, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [3.2, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [33.8, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { d: 'M7 20.2 L33 20.2 Q34.4 20.2 34.4 21.6 L34.4 67.8 Q34.4 69.2 33 69.2 L7 69.2 Q5.6 69.2 5.6 67.8 L5.6 21.6 Q5.6 20.2 7 20.2 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M20 3 C26.5 3 30.5 3.3 31.5 5 Q32 6.4 32 8.5 L32 17 Q32 18.4 30.6 18.4 L9.4 18.4 Q8 18.4 8 17 L8 8.5 Q8 6.4 8.5 5 C9.5 3.3 13.5 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M8 6.2 L4.8 5.8 Q4 5.8 4 6.6 L4 9.6 Q4 10.2 4.8 10.2 L8 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M32 6.2 L35.2 5.8 Q36 5.8 36 6.6 L36 9.6 Q36 10.2 35.2 10.2 L32 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M10 5.8 Q20 4.2 30 5.8 L29.4 9.8 Q20 8.8 10.6 9.8 Z', fill: 'glass' },
    ],
  },
  watertanker: {
    full: [
      { rect: [5.6, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [31.4, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [4.2, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [32.8, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [4.2, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [32.8, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { d: 'M13 20.4 L27 20.4 C31 20.4 33 22 33 25.4 L33 64 C33 67.4 31 69 27 69 L13 69 C9 69 7 67.4 7 64 L7 25.4 C7 22 9 20.4 13 20.4 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M20 3 C26.5 3 30.5 3.3 31.5 5 Q32 6.4 32 8.5 L32 17 Q32 18.4 30.6 18.4 L9.4 18.4 Q8 18.4 8 17 L8 8.5 Q8 6.4 8.5 5 C9.5 3.3 13.5 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M8 6.2 L4.8 5.8 Q4 5.8 4 6.6 L4 9.6 Q4 10.2 4.8 10.2 L8 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M32 6.2 L35.2 5.8 Q36 5.8 36 6.6 L36 9.6 Q36 10.2 35.2 10.2 L32 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M10 5.8 Q20 4.2 30 5.8 L29.4 9.8 Q20 8.8 10.6 9.8 Z', fill: 'glass' },
      { circle: [20, 44.6, 3.6], fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M20 24 L20 40.2', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M20 49 L20 65.4', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M7.6 33 L32.4 33', stroke: 'keyline', strokeWidth: 0.6 },
      { d: 'M7.6 56.4 L32.4 56.4', stroke: 'keyline', strokeWidth: 0.6 },
      { d: 'M9 4.8 L13.6 3.6 L13.6 4.4 L9.3 5.7 Z', fill: 'keyline' },
      { d: 'M31 4.8 L26.4 3.6 L26.4 4.4 L30.7 5.7 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [5.6, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [31.4, 9.6, 3, 8, 1.2], fill: 'glass' },
      { rect: [4.2, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [32.8, 49, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [4.2, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { rect: [32.8, 57.6, 3, 7.4, 1.2], fill: 'glass' },
      { d: 'M13 20.4 L27 20.4 C31 20.4 33 22 33 25.4 L33 64 C33 67.4 31 69 27 69 L13 69 C9 69 7 67.4 7 64 L7 25.4 C7 22 9 20.4 13 20.4 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M20 3 C26.5 3 30.5 3.3 31.5 5 Q32 6.4 32 8.5 L32 17 Q32 18.4 30.6 18.4 L9.4 18.4 Q8 18.4 8 17 L8 8.5 Q8 6.4 8.5 5 C9.5 3.3 13.5 3 20 3 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M8 6.2 L4.8 5.8 Q4 5.8 4 6.6 L4 9.6 Q4 10.2 4.8 10.2 L8 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M32 6.2 L35.2 5.8 Q36 5.8 36 6.6 L36 9.6 Q36 10.2 35.2 10.2 L32 9.8 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M10 5.8 Q20 4.2 30 5.8 L29.4 9.8 Q20 8.8 10.6 9.8 Z', fill: 'glass' },
      { circle: [20, 44.6, 3.6], fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
    ],
  },
  bus: {
    full: [
      { rect: [4.6, 10, 2.8, 9, 1.2], fill: 'glass' },
      { rect: [32.6, 10, 2.8, 9, 1.2], fill: 'glass' },
      { rect: [4.6, 50, 2.8, 9, 1.2], fill: 'glass' },
      { rect: [32.6, 50, 2.8, 9, 1.2], fill: 'glass' },
      { d: 'M11 2.6 L29 2.6 Q33.4 2.6 33.4 7 L33.4 67 Q33.4 69.6 30.8 69.6 L9.2 69.6 Q6.6 69.6 6.6 67 L6.6 7 Q6.6 2.6 11 2.6 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 7.2 C5.2 7.4 3.6 8.3 3.2 9.5 C3 10.3 3.7 10.7 4.8 10.6 L6.7 10.4 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 7.2 C34.8 7.4 36.4 8.3 36.8 9.5 C37 10.3 36.3 10.7 35.2 10.6 L33.3 10.4 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 4.8 Q20 3.6 31.2 4.8 L30.8 9.8 Q20 9.2 9.2 9.8 Z', fill: 'glass' },
      { rect: [13.6, 27, 12.8, 11, 1.2], fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8 13 L9.4 13 L9.4 20.6 L8 20.6 Z', fill: 'glass' },
      { d: 'M32 13 L30.6 13 L30.6 20.6 L32 20.6 Z', fill: 'glass' },
      { d: 'M8 21.6 L9.4 21.6 L9.4 29.200000000000003 L8 29.200000000000003 Z', fill: 'glass' },
      { d: 'M32 21.6 L30.6 21.6 L30.6 29.200000000000003 L32 29.200000000000003 Z', fill: 'glass' },
      { d: 'M8 30.2 L9.4 30.2 L9.4 37.8 L8 37.8 Z', fill: 'glass' },
      { d: 'M32 30.2 L30.6 30.2 L30.6 37.8 L32 37.8 Z', fill: 'glass' },
      { d: 'M8 38.8 L9.4 38.8 L9.4 46.4 L8 46.4 Z', fill: 'glass' },
      { d: 'M32 38.8 L30.6 38.8 L30.6 46.4 L32 46.4 Z', fill: 'glass' },
      { d: 'M8 47.4 L9.4 47.4 L9.4 55 L8 55 Z', fill: 'glass' },
      { d: 'M32 47.4 L30.6 47.4 L30.6 55 L32 55 Z', fill: 'glass' },
      { d: 'M8 56 L9.4 56 L9.4 63.6 L8 63.6 Z', fill: 'glass' },
      { d: 'M32 56 L30.6 56 L30.6 63.6 L32 63.6 Z', fill: 'glass' },
      { rect: [16, 46, 8, 5, 0.8], fill: 'glass' },
      { rect: [16, 56, 8, 5, 0.8], fill: 'glass' },
      { d: 'M8.2 3.8 L12 3.3 L12 4 L8.4 4.6 Z', fill: 'keyline' },
      { d: 'M31.8 3.8 L28 3.3 L28 4 L31.6 4.6 Z', fill: 'keyline' },
      { d: 'M7.6 65 L8.4 65 L8.4 68.6 L7.6 68.2 Z', fill: 'keyline' },
      { d: 'M32.4 65 L31.6 65 L31.6 68.6 L32.4 68.2 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [4.6, 10, 2.8, 9, 1.2], fill: 'glass' },
      { rect: [32.6, 10, 2.8, 9, 1.2], fill: 'glass' },
      { rect: [4.6, 50, 2.8, 9, 1.2], fill: 'glass' },
      { rect: [32.6, 50, 2.8, 9, 1.2], fill: 'glass' },
      { d: 'M11 2.6 L29 2.6 Q33.4 2.6 33.4 7 L33.4 67 Q33.4 69.6 30.8 69.6 L9.2 69.6 Q6.6 69.6 6.6 67 L6.6 7 Q6.6 2.6 11 2.6 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M6.7 7.2 C5.2 7.4 3.6 8.3 3.2 9.5 C3 10.3 3.7 10.7 4.8 10.6 L6.7 10.4 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M33.3 7.2 C34.8 7.4 36.4 8.3 36.8 9.5 C37 10.3 36.3 10.7 35.2 10.6 L33.3 10.4 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
      { d: 'M8.8 4.8 Q20 3.6 31.2 4.8 L30.8 9.8 Q20 9.2 9.2 9.8 Z', fill: 'glass' },
      { rect: [13.6, 27, 12.8, 11, 1.2], fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
    ],
  },
  motorbike: {
    full: [
      { rect: [18.1, 3.5, 3.8, 11, 1.9], fill: 'glass' },
      { rect: [18.3, 61.5, 3.4, 8.5, 1.7], fill: 'glass' },
      { d: 'M20 20.6 C23.6 20.6 26.2 23.4 26.3 27.6 C26.4 31.6 25.2 35.4 23.8 38.4 C24.8 40.2 25.4 43 25.3 47 C25.2 52 24.4 57 23.2 61 L22.4 65.4 Q20 66.4 17.6 65.4 L16.8 61 C15.6 57 14.8 52 14.7 47 C14.6 43 15.2 40.2 16.2 38.4 C14.8 35.4 13.6 31.6 13.7 27.6 C13.8 23.4 16.4 20.6 20 20.6 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M20 13 C22.6 13 24 14.4 24 16.4 C24 18.6 22.4 20 20 20 C17.6 20 16 18.6 16 16.4 C16 14.4 17.4 13 20 13 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.2 },
      { d: 'M6.4 21.4 C11 20.6 15.5 20.2 20 20.2 C24.5 20.2 29 20.6 33.6 21.4 L33.4 23.4 C28.9 22.7 24.4 22.3 20 22.3 C15.6 22.3 11.1 22.7 6.6 23.4 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.6 },
      { d: 'M8.6 21.2 L6.8 18.8 L7.6 18.4 L9.4 20.8 Z', fill: 'glass' },
      { d: 'M31.4 21.2 L33.2 18.8 L32.4 18.4 L30.6 20.8 Z', fill: 'glass' },
      { d: 'M4 17.6 A1.8 2.4 0 1 0 7.6 17.6 A1.8 2.4 0 1 0 4 17.6 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.6 },
      { d: 'M32.400000000000006 17.6 A1.8 2.4 0 1 0 36 17.6 A1.8 2.4 0 1 0 32.400000000000006 17.6 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.6 },
      { d: 'M16.3 38.6 Q20 37 23.7 38.6', stroke: 'keyline', strokeWidth: 1 },
      { d: 'M15.2 50.2 Q20 49 24.8 50.2', stroke: 'keyline', strokeWidth: 1 },
      { circle: [20, 28.6, 2], fill: 'keyline' },
      { d: 'M18.2 64 L21.8 64 L21.5 65.1 L18.5 65.1 Z', fill: 'keyline' },
    ],
    sm: [
      { rect: [17.9, 3.2, 4.2, 11.2, 2.1], fill: 'glass' },
      { rect: [18.1, 61.3, 3.8, 8.7, 1.9], fill: 'glass' },
      { d: 'M20 20.6 C24 20.6 27 23.4 27.1 27.6 C27.2 31.6 25.8 35.4 24.3 38.4 C25.4 40.2 26 43 25.9 47 C25.8 52 24.9 57 23.6 61 L22.7 65.4 Q20 66.5 17.3 65.4 L16.4 61 C15.1 57 14.2 52 14.1 47 C14 43 14.6 40.2 15.7 38.4 C14.2 35.4 12.8 31.6 12.9 27.6 C13 23.4 16 20.6 20 20.6 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
      { d: 'M20 12.8 C22.9 12.8 24.5 14.3 24.5 16.4 C24.5 18.7 22.7 20.1 20 20.1 C17.3 20.1 15.5 18.7 15.5 16.4 C15.5 14.3 17.1 12.8 20 12.8 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.2 },
      { d: 'M5.2 21.2 C10.1 20.4 15 20 20 20 C25 20 29.9 20.4 34.8 21.2 L34.6 23.6 C29.7 22.8 24.9 22.4 20 22.4 C15.1 22.4 10.3 22.8 5.4 23.6 Z', fill: 'glass', stroke: 'keyline', strokeWidth: 0.8 },
    ],
  },
  generic: {
    full: [
      { d: 'M20 12 C20.9 12 21.5 12.6 21.8 13.4 L33.4 55.4 C33.8 57 32.2 58.2 30.8 57.4 L20.8 51.2 C20.3 50.9 19.7 50.9 19.2 51.2 L9.2 57.4 C7.8 58.2 6.2 57 6.6 55.4 L18.2 13.4 C18.5 12.6 19.1 12 20 12 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
    ],
    sm: [
      { d: 'M20 12 C20.9 12 21.5 12.6 21.8 13.4 L33.4 55.4 C33.8 57 32.2 58.2 30.8 57.4 L20.8 51.2 C20.3 50.9 19.7 50.9 19.2 51.2 L9.2 57.4 C7.8 58.2 6.2 57 6.6 55.4 L18.2 13.4 C18.5 12.6 19.1 12 20 12 Z', fill: 'body', stroke: 'keyline', strokeWidth: 1.5 },
    ],
  },
  // END GENERATED vehicle glyphs
  threewheel: {
    // THREE-WHEELER (Bajaj / tuk-tuk). Geometry from Mahad's artboard,
    // docs/design/tuktuk-marker-2026-09-21-v3.dc.html — the FIRST glyph in this file
    // whose BOTH forms were drawn by a designer rather than derived here. The
    // artboard names them itself: "Large geometry · >= 40 px · front wheel +
    // mirrors" and "Small geometry · 24-32 px · wedge + rear wheels + front wheel,
    // no mirrors". Those two lists are reproduced verbatim below.
    //
    // 🔴 WHAT CARRIES IT UNDER ROTATION. Markers only ever render rotated to course,
    // so the cue must be an outline property at every angle. Measured scanline
    // widths (viewBox units) say the BODY alone does not do it — from the nose to
    // y~36 this body and the CAR body agree to within half a unit, and the
    // front/rear taper is 0.83 against the 0.52 of the glyph it replaces.
    //
    // Two things carry it instead. First the REAR WHEELS, which protrude to the full
    // 0->40 of the viewBox at the tail where the car reaches 34.2 and the bus 33.5 —
    // a tail six units wider than anything else in the fleet, and a property of the
    // outline at every angle. Second, and only visible once rendered: the CAR's glass
    // canopy fills most of its body, while this glyph is mostly body colour with a
    // narrow band at the nose. The two read apart by how much of the shape is dark,
    // which no width table can see.
    //
    // 🔴 A METRIC BUILT FOR THIS WAS WRONG; DO NOT REBUILD IT. A rotated mask-IoU
    // comparison rated this glyph 0.87 against car and called it a regression. The
    // control refuted the instrument: the same metric rates CAR vs BUS at 0.927, a
    // pair a fresh blind judge scored 4/4 at DPR1 and DPR2 with zero confusion. Bulk
    // overlap is not the separating property; end shape and glass mass are.
    full: [
      // Rear axle — the two wheels protruding at the tail. The identity cue, so they
      // are drawn FIRST and the body sits over their inboard halves.
      { rect: [0, 48, 7, 16, 2], fill: 'glass' },
      { rect: [33, 48, 7, 16, 2], fill: 'glass' },
      // Single centred front wheel under the nose — the "one at the front" half of
      // the three-wheeler read. Inside the outline, so it adds no width.
      { rect: [17, 1, 6, 13, 3], fill: 'glass' },
      // Side mirrors. Drawn before the body so it covers their inboard ends and only
      // the protruding nubs show. LARGE FORM ONLY — at 24 px a 5x3 nub is under half
      // a pixel and turns to mud, which is why the artboard's small geometry omits
      // them and so does `sm`.
      { rect: [3, 21, 5, 3, 1], fill: 'glass' },
      { rect: [32, 21, 5, 3, 1], fill: 'glass' },
      // Body: rounded nose at (20,8) widening to a flat tail. Verbatim from the
      // artboard. 🔴 The artboard's stroke-width 1.5 is NOT carried over: it is drawn
      // at 60x108 there and we rasterise at 24-40 px, where this file's established
      // keyline weights (2 / 2.6) are what hold the shape against a satellite
      // basemap. Geometry is the designer's; keyline weight is not.
      { d: 'M20 8 C27 8 32 11 33 18 L36 60 Q36 68 28 68 L12 68 Q4 68 4 60 L7 18 C8 11 13 8 20 8 Z', fill: 'body', stroke: 'keyline', strokeWidth: 2 },
      // Windscreen band across the nose.
      { d: 'M10 20 Q20 15 30 20 L31 28 Q20 24 9 28 Z', fill: 'glass' },
      // Rear window band. LARGE FORM ONLY, same reasoning as the mirrors.
      { d: 'M9 62 Q20 60 31 62 L31 65 Q20 63 9 65 Z', fill: 'glass' },
    ],
    // 🔴 SUPPLIED BY THE DESIGNER, NOT DERIVED. An earlier revision of this file
    // derived `sm` here and dropped the front wheel on the assumption it merged into
    // the nose. The artboard keeps it, and the artboard wins: it is drawn at the
    // size it is drawn for. What the small form drops is the MIRRORS and the REAR
    // BAND — both sub-pixel at 24 px — and nothing else.
    sm: [
      { rect: [0, 48, 7, 16, 2], fill: 'glass' },
      { rect: [33, 48, 7, 16, 2], fill: 'glass' },
      { rect: [17, 1, 6, 13, 3], fill: 'glass' },
      { d: 'M20 8 C27 8 32 11 33 18 L36 60 Q36 68 28 68 L12 68 Q4 68 4 60 L7 18 C8 11 13 8 20 8 Z', fill: 'body', stroke: 'keyline', strokeWidth: 2.6 },
      { d: 'M10 20 Q20 15 30 20 L31 28 Q20 24 9 28 Z', fill: 'glass' },
    ],
  },
  dot: {
    // The map draws the dot with a circle LAYER, not this image; kept here so the
    // atlas can produce any glyph across the full matrix (tested), and the two
    // never disagree about what a dot is.
    full: [
      { circle: [20, 36, 14], fill: 'body', stroke: 'keyline', strokeWidth: 2.6 },
      { circle: [20, 32, 7], fill: 'keyline' },
    ],
    sm: [
      { circle: [20, 36, 14], fill: 'body', stroke: 'keyline', strokeWidth: 2.6 },
      { circle: [20, 32, 7], fill: 'keyline' },
    ],
  },
};

/** The three colours one rasterised glyph needs. `body` is the tone's resolved
 *  colour (a stopped car is slate, a moving car green); glass/keyline are neutral. */
export interface GlyphColors {
  body: string;
  glass: string;
  keyline: string;
}

function colorForRole(role: FillRole, colors: GlyphColors): string {
  return role === 'body' ? colors.body : role === 'glass' ? colors.glass : colors.keyline;
}

function opToPath(op: DrawOp): Path2D {
  if (op.d) return new Path2D(op.d);
  const p = new Path2D();
  if (op.rect) {
    const [x, y, w, h, r] = op.rect;
    p.roundRect(x, y, w, h, r);
  } else if (op.circle) {
    const [cx, cy, r] = op.circle;
    p.arc(cx, cy, r, 0, Math.PI * 2);
  }
  return p;
}

/**
 * Rasterise one glyph form to an `ImageData` at `ICON_BASE_PX` height (× dpr), with
 * the given colours. Runtime-only (needs a real 2D canvas); the pure inputs —
 * geometry, colour resolution, size/glyph selection — are what the tests cover.
 * Keyline strokes are painted behind their fill (paint-order:stroke) so they read
 * as an outer halo, matching the reference construction.
 */
export function rasterizeGlyph(glyph: Glyph, form: 'full' | 'sm', colors: GlyphColors, dpr = 1): ImageData | null {
  const scale = (ICON_BASE_PX * dpr) / GLYPH_VIEWBOX.h;
  const wPx = Math.ceil(GLYPH_VIEWBOX.w * scale);
  const hPx = Math.ceil(GLYPH_VIEWBOX.h * scale);
  const canvas = document.createElement('canvas');
  canvas.width = wPx;
  canvas.height = hPx;
  // `willReadFrequently` keeps this canvas CPU-backed: the getImageData below is
  // then a plain memory read, never a synchronous GPU read-back that stalls the
  // main thread while the map's WebGL context is busy uploading tiles.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.lineJoin = 'round';
  for (const op of GLYPHS[glyph][form]) {
    const path = opToPath(op);
    if (op.stroke && op.strokeWidth) {
      ctx.lineWidth = op.strokeWidth;
      ctx.strokeStyle = colorForRole(op.stroke, colors);
      ctx.stroke(path);
    }
    if (op.fill) {
      ctx.fillStyle = colorForRole(op.fill, colors);
      ctx.fill(path, op.fillRule ?? 'nonzero');
    }
  }
  return ctx.getImageData(0, 0, wPx, hPx);
}

export interface AtlasEntry {
  id: string;
  glyph: Glyph;
  form: 'full' | 'sm';
  paintClass: MarkerPaintClass;
  colors: GlyphColors;
}

/**
 * One atlas descriptor for any (glyph × PAINT CLASS × form) — the atlas builder's
 * unit, carrying the colours already resolved for that class (every class is SOLID —
 * see `colorsForPaintClass`). Being able to produce ANY combination is what the
 * full-matrix test checks; the MAP asks for the four paint classes × the drawn
 * glyphs (see `silhouetteImageEntries`).
 */
export function atlasEntry(
  glyph: Glyph,
  paintClass: MarkerPaintClass,
  form: 'full' | 'sm',
  colors: MarkerColors,
): AtlasEntry {
  return {
    id: `veh-${glyph}-${paintClass}-${form}`,
    glyph,
    form,
    paintClass,
    colors: colorsForPaintClass(paintClass, colors),
  };
}

/**
 * Exactly the images the map instantiates: every drawn silhouette (6), in every
 * PAINT CLASS (moving/idling/still/offline/speeding — 5), both forms (2) → 60
 * images. Slice B took this 48 → 60: ALL 12 new images are reachable, because any
 * vehicle type can speed — the moving-only constraint is on the vehicle's motion
 * STATE, not on which silhouette may carry the class. The matrix is kept complete
 * regardless, because `parseSilhouetteId` must round-trip every id the builder
 * emits (the slice-55 `threewheel` omission is exactly what a partial matrix costs). Since
 * 56c the whole fleet draws a silhouette (stale/offline included, all FILLED);
 * the DOT survives only for `other`/unclassified, which is not a drawn glyph, so no
 * silhouette ships for it. The count is FLAT vs the pre-56c 4-tone atlas (48 → 48):
 * stale/offline no longer render dots, but stopped/stale/unknown collapse to one
 * `still` image, so the naive 6-tone × 6-glyph × 2 = 72 is AVOIDED. Colour is the
 * paint class's; size is not (that is zoom-only).
 */
export function silhouetteImageEntries(colors: MarkerColors): AtlasEntry[] {
  const out: AtlasEntry[] = [];
  for (const glyph of SILHOUETTE_GLYPHS) {
    for (const pc of MARKER_PAINT_CLASSES) {
      out.push(atlasEntry(glyph, pc, 'full', colors));
      out.push(atlasEntry(glyph, pc, 'sm', colors));
    }
  }
  return out;
}
